import { create } from "zustand";
import { persist } from "zustand/middleware";
import { postDark } from "@client/api/endpoints";
import { useUserStore } from "./user";

interface DarkStore {
  /** null = follow user.darkMode + system; boolean = explicit override (uložené v localStorage). */
  override: boolean | null;
  setOverride: (v: boolean | null) => void;
  toggleDark: (state?: boolean) => Promise<void>;
}

export const useDarkStore = create<DarkStore>()(
  persist(
    (set, get) => ({
      override: null,
      setOverride: (v) => set({ override: v }),

      toggleDark: async (state) => {
        const target = state ?? !computeEffectiveDark();
        set({ override: target });
        try {
          await postDark({ dark: target });
        } catch {
          // Ponecháme override - uživatel viděl změnu, jen nesynchronizovala s DB.
          // Příští login načte z DB znovu.
        }
        void get; // unused but keeps closure type stable
      },
    }),
    {
      name: "demirel-dark-override",
      partialize: (s) => ({ override: s.override }),
    }
  )
);

const getSystemDark = (): boolean => {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
};

/**
 * Vypočítá efektivní hodnotu dark módu z override / user.darkMode / systemPref.
 * Voláno z useDark hook + při toggle.
 */
export const computeEffectiveDark = (): boolean => {
  const { override } = useDarkStore.getState();
  if (override !== null) return override;
  const { user } = useUserStore.getState();
  if (user) {
    const u = user as UserSafe;
    if (u.darkMode === true) return true;
    if (u.darkMode === false) return false;
  }
  return getSystemDark();
};

/** React hook - vrací aktuální dark stav, reaguje na změny override + user. */
import { useEffect, useState } from "react";

export const useDark = (): boolean => {
  const override = useDarkStore((s) => s.override);
  const user = useUserStore((s) => s.user);
  const [systemDark, setSystemDark] = useState(getSystemDark);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  if (override !== null) return override;
  // user může být UserSafe | null | false; jen pokud je to objekt, čteme darkMode
  if (user && typeof user === "object") {
    if (user.darkMode === true) return true;
    if (user.darkMode === false) return false;
  }
  return systemDark;
};
